import { getFitContentRect, type ContentRect } from './map-viewport'
import type { RemoteViewHost } from './remote-view-host'

export const DRAG_SYNCED_ATTR = 'data-xmax-drag-synced'

export type DragVideoSyncOptions = {
  getTargetSize: () => [number, number]
  getFitMode: () => 'contain' | 'cover'
  onSync?: () => void
}

export type RemoteDragLayout = {
  /** Drawable overlay aligned to the painted RTC media on screen. */
  overlay: ContentRect
  /** Pointer → session coordinate mapping bounds. */
  mapping: ContentRect
  /** Hit-test bounds inside the preview frame. */
  hitRect: ContentRect
  source: 'media' | 'intrinsic' | 'fit'
}

function toContentRect(rect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>): ContentRect {
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
  }
}

export function isMediaInsetInContainer(
  mediaRect: Pick<DOMRect, 'width' | 'height'>,
  containerRect: Pick<DOMRect, 'width' | 'height'>,
): boolean {
  if (!mediaRect.width || !mediaRect.height || !containerRect.width || !containerRect.height) {
    return false
  }
  const widthRatio = mediaRect.width / containerRect.width
  const heightRatio = mediaRect.height / containerRect.height
  return widthRatio < 0.98 || heightRatio < 0.98
}

export function intersectContentRects(a: ContentRect, b: ContentRect): ContentRect {
  const left = Math.max(a.left, b.left)
  const top = Math.max(a.top, b.top)
  const right = Math.min(a.left + a.width, b.left + b.width)
  const bottom = Math.min(a.top + a.height, b.top + b.height)
  return {
    left,
    top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  }
}

export function readMediaIntrinsicSize(
  media: HTMLVideoElement | HTMLCanvasElement,
): [number, number] | null {
  const video = media as HTMLVideoElement
  if ('videoWidth' in video && video.videoWidth > 0 && video.videoHeight > 0) {
    return [video.videoWidth, video.videoHeight]
  }

  if (media.width > 0 && media.height > 0) {
    return [media.width, media.height]
  }
  return null
}

export function resolveMediaObjectFit(
  media: HTMLElement,
  fallback: 'contain' | 'cover',
): 'contain' | 'cover' | 'fill' {
  if (typeof getComputedStyle !== 'function') {
    return fallback
  }

  const fit = getComputedStyle(media).objectFit
  if (fit === 'contain' || fit === 'cover' || fit === 'fill') {
    return fit
  }
  return fallback
}

export function resolveRemoteDragLayout(input: {
  wrapperRect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>
  mediaRect: Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>
  media: HTMLVideoElement | HTMLCanvasElement | null
  targetSize: [number, number]
  fitMode: 'contain' | 'cover'
}): RemoteDragLayout | null {
  const { wrapperRect, mediaRect, media, targetSize, fitMode } = input
  if (!wrapperRect.width || !wrapperRect.height || !mediaRect.width || !mediaRect.height) {
    return null
  }

  const wrapper = toContentRect(wrapperRect)

  // Upload / RTC FIT: map linearly across the media layout box. Hit-test only
  // the visible portion inside the preview frame (wide images overflow horizontally).
  if (fitMode === 'contain') {
    const overlay = toContentRect(mediaRect)
    const hitRect = intersectContentRects(overlay, wrapper)
    const drawable = hitRect.width > 0 && hitRect.height > 0 ? hitRect : wrapper
    return { overlay: drawable, mapping: overlay, hitRect: drawable, source: 'media' }
  }

  if (isMediaInsetInContainer(mediaRect, wrapperRect)) {
    const overlay = toContentRect(mediaRect)
    return { overlay, mapping: overlay, hitRect: overlay, source: 'media' }
  }

  const intrinsic = media ? readMediaIntrinsicSize(media) : null
  const frameSize = intrinsic ?? targetSize
  const objectFit = media ? resolveMediaObjectFit(media, fitMode) : fitMode

  if (objectFit === 'fill') {
    const overlay = toContentRect(mediaRect)
    return { overlay, mapping: overlay, hitRect: wrapper, source: 'intrinsic' }
  }

  const mapping = getFitContentRect(mediaRect, frameSize, objectFit)
  const overlay = intersectContentRects(mapping, wrapper)
  const drawable = overlay.width > 0 && overlay.height > 0 ? overlay : wrapper
  return {
    overlay: drawable,
    mapping,
    hitRect: wrapper,
    source: intrinsic ? 'intrinsic' : 'fit',
  }
}

function resetDragHostFullBleed(dragHost: HTMLElement): void {
  dragHost.style.inset = '0'
  dragHost.style.left = ''
  dragHost.style.top = ''
  dragHost.style.right = ''
  dragHost.style.bottom = ''
  dragHost.style.width = ''
  dragHost.style.height = ''
  dragHost.removeAttribute(DRAG_SYNCED_ATTR)
}

/** Align drag overlay to the painted RTC media region on screen. */
export function syncDragHostToVideo(
  host: RemoteViewHost,
  targetSize: [number, number],
  fitMode: 'contain' | 'cover',
): boolean {
  const { wrapper, videoHost, dragHost } = host
  const media = videoHost.querySelector('video, canvas') as HTMLVideoElement | HTMLCanvasElement | null
  if (!media) {
    resetDragHostFullBleed(dragHost)
    return false
  }

  const wrapperRect = wrapper.getBoundingClientRect()
  const mediaRect = media.getBoundingClientRect()
  const layout = resolveRemoteDragLayout({
    wrapperRect,
    mediaRect,
    media,
    targetSize,
    fitMode,
  })
  if (!layout || !layout.overlay.width || !layout.overlay.height) {
    resetDragHostFullBleed(dragHost)
    return false
  }

  const { overlay } = layout
  dragHost.style.inset = 'auto'
  dragHost.style.left = `${overlay.left - wrapperRect.left}px`
  dragHost.style.top = `${overlay.top - wrapperRect.top}px`
  dragHost.style.width = `${overlay.width}px`
  dragHost.style.height = `${overlay.height}px`
  dragHost.style.right = ''
  dragHost.style.bottom = ''
  dragHost.setAttribute(DRAG_SYNCED_ATTR, '')
  return true
}

function resetDragSurfaceFullBleed(dragSurface: HTMLElement): void {
  dragSurface.style.inset = '0'
  dragSurface.style.left = ''
  dragSurface.style.top = ''
  dragSurface.style.right = ''
  dragSurface.style.bottom = ''
  dragSurface.style.width = ''
  dragSurface.style.height = ''
  dragSurface.removeAttribute(DRAG_SYNCED_ATTR)
}

function applyDragSurfaceOverlayStyles(
  dragSurface: HTMLElement,
  overlay: ContentRect,
  wrapperRect: Pick<DOMRect, 'left' | 'top'>,
): boolean {
  const nextLeft = `${overlay.left - wrapperRect.left}px`
  const nextTop = `${overlay.top - wrapperRect.top}px`
  const nextWidth = `${overlay.width}px`
  const nextHeight = `${overlay.height}px`

  if (
    dragSurface.hasAttribute(DRAG_SYNCED_ATTR)
    && dragSurface.style.inset === 'auto'
    && dragSurface.style.left === nextLeft
    && dragSurface.style.top === nextTop
    && dragSurface.style.width === nextWidth
    && dragSurface.style.height === nextHeight
  ) {
    return false
  }

  dragSurface.style.inset = 'auto'
  dragSurface.style.left = nextLeft
  dragSurface.style.top = nextTop
  dragSurface.style.width = nextWidth
  dragSurface.style.height = nextHeight
  dragSurface.style.right = ''
  dragSurface.style.bottom = ''
  dragSurface.setAttribute(DRAG_SYNCED_ATTR, '')
  return true
}

/** Align drag overlay to the painted RTC media region. */
export function syncDragSurfaceToVideo(
  dragSurface: HTMLElement,
  targetSize: [number, number],
  fitMode: 'contain' | 'cover',
): boolean {
  const wrapper = dragSurface.parentElement
  if (!wrapper) {
    resetDragSurfaceFullBleed(dragSurface)
    return false
  }

  const layout = resolveRemoteDragLayoutFromSurface(dragSurface, targetSize, fitMode)
  if (!layout || !layout.overlay.width || !layout.overlay.height) {
    resetDragSurfaceFullBleed(dragSurface)
    return false
  }

  const wrapperRect = wrapper.getBoundingClientRect()
  const { overlay } = layout
  return applyDragSurfaceOverlayStyles(dragSurface, overlay, wrapperRect)
}

export function resolveRemoteDragLayoutFromSurface(
  dragSurface: HTMLElement,
  targetSize: [number, number],
  fitMode: 'contain' | 'cover',
): RemoteDragLayout | null {
  const wrapper = dragSurface.parentElement
  if (!wrapper) {
    return null
  }

  const videoHost = wrapper.querySelector('[data-xmax-remote-video]') as HTMLElement | null
  if (!videoHost) {
    return null
  }

  const media = videoHost.querySelector('video, canvas') as HTMLVideoElement | HTMLCanvasElement | null
  if (!media) {
    return null
  }

  return resolveRemoteDragLayout({
    wrapperRect: wrapper.getBoundingClientRect(),
    mediaRect: media.getBoundingClientRect(),
    media,
    targetSize,
    fitMode,
  })
}

export function observeDragHostVideoSync(
  host: RemoteViewHost,
  options: DragVideoSyncOptions,
): () => void {
  const mediaListeners = new Map<EventTarget, Array<() => void>>()

  const bindMediaListeners = () => {
    const media = host.videoHost.querySelector('video, canvas')
    if (!media || mediaListeners.has(media)) {
      return
    }

    const rerender = () => apply()
    const bindings: Array<() => void> = []
    const add = (type: string) => {
      media.addEventListener(type, rerender)
      bindings.push(() => media.removeEventListener(type, rerender))
    }

    add('loadedmetadata')
    add('loadeddata')
    add('resize')
    mediaListeners.set(media, bindings)
  }

  const apply = () => {
    syncDragHostToVideo(host, options.getTargetSize(), options.getFitMode())
    options.onSync?.()
    bindMediaListeners()
  }

  apply()

  const resizeObserver = typeof ResizeObserver !== 'undefined'
    ? new ResizeObserver(() => apply())
    : null
  resizeObserver?.observe(host.wrapper)
  resizeObserver?.observe(host.videoHost)

  const observeMedia = () => {
    const media = host.videoHost.querySelector('video, canvas')
    if (media) {
      resizeObserver?.observe(media)
    }
  }
  observeMedia()

  const mutationObserver = typeof MutationObserver !== 'undefined'
    ? new MutationObserver(() => {
      observeMedia()
      apply()
    })
    : null
  mutationObserver?.observe(host.videoHost, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class', 'width', 'height'],
  })

  const onWindowChange = () => apply()
  window.addEventListener('resize', onWindowChange)
  window.addEventListener('scroll', onWindowChange, true)

  return () => {
    resizeObserver?.disconnect()
    mutationObserver?.disconnect()
    for (const unbind of Array.from(mediaListeners.values())) {
      unbind.forEach((dispose) => dispose())
    }
    mediaListeners.clear()
    window.removeEventListener('resize', onWindowChange)
    window.removeEventListener('scroll', onWindowChange, true)
    resetDragHostFullBleed(host.dragHost)
  }
}

/** Keep a drag surface aligned with remote output video layout. */
export function observeDragSurfaceVideoSync(
  dragSurface: HTMLElement,
  options: DragVideoSyncOptions,
): () => void {
  const wrapper = dragSurface.parentElement
  if (!wrapper) {
    return () => {}
  }

  const mediaListeners = new Map<EventTarget, Array<() => void>>()
  let rafId: number | null = null
  let observedVideoHost: Element | null = null

  const bindMediaListeners = (media: EventTarget) => {
    if (mediaListeners.has(media)) {
      return
    }

    const rerender = () => scheduleApply()
    const bindings: Array<() => void> = []
    const add = (type: string) => {
      media.addEventListener(type, rerender)
      bindings.push(() => media.removeEventListener(type, rerender))
    }

    add('loadedmetadata')
    add('loadeddata')
    add('resize')
    mediaListeners.set(media, bindings)
  }

  const apply = () => {
    const changed = syncDragSurfaceToVideo(dragSurface, options.getTargetSize(), options.getFitMode())
    if (changed) {
      options.onSync?.()
    }

    const videoHost = wrapper.querySelector('[data-xmax-remote-video]')
    const media = videoHost?.querySelector('video, canvas')
    if (media) {
      bindMediaListeners(media)
    }
  }

  const scheduleApply = () => {
    if (rafId !== null) {
      return
    }
    rafId = requestAnimationFrame(() => {
      rafId = null
      apply()
    })
  }

  const bindVideoHostObserver = (videoHostObserver: MutationObserver | null) => {
    const host = wrapper.querySelector('[data-xmax-remote-video]')
    if (!host || host === observedVideoHost || !videoHostObserver) {
      return
    }

    videoHostObserver.disconnect()
    observedVideoHost = host
    videoHostObserver.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'width', 'height'],
    })
  }

  apply()

  const resizeObserver = typeof ResizeObserver !== 'undefined'
    ? new ResizeObserver(() => scheduleApply())
    : null
  resizeObserver?.observe(wrapper)

  const videoHost = wrapper.querySelector('[data-xmax-remote-video]')
  if (videoHost) {
    resizeObserver?.observe(videoHost)
  }

  const observeMedia = () => {
    const host = wrapper.querySelector('[data-xmax-remote-video]')
    const media = host?.querySelector('video, canvas')
    if (media) {
      resizeObserver?.observe(media)
      bindMediaListeners(media)
    }
  }
  observeMedia()

  const videoHostMutationObserver = typeof MutationObserver !== 'undefined'
    ? new MutationObserver(() => {
      observeMedia()
      scheduleApply()
    })
    : null
  bindVideoHostObserver(videoHostMutationObserver)

  // Child-list only on wrapper — never watch dragSurface style (would loop with sync).
  const wrapperMutationObserver = typeof MutationObserver !== 'undefined'
    ? new MutationObserver(() => {
      bindVideoHostObserver(videoHostMutationObserver)
      observeMedia()
      scheduleApply()
    })
    : null
  wrapperMutationObserver?.observe(wrapper, {
    childList: true,
    subtree: true,
  })

  const onWindowChange = () => scheduleApply()
  window.addEventListener('resize', onWindowChange)
  window.addEventListener('scroll', onWindowChange, true)

  return () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
    resizeObserver?.disconnect()
    wrapperMutationObserver?.disconnect()
    videoHostMutationObserver?.disconnect()
    for (const unbind of Array.from(mediaListeners.values())) {
      unbind.forEach((dispose) => dispose())
    }
    mediaListeners.clear()
    window.removeEventListener('resize', onWindowChange)
    window.removeEventListener('scroll', onWindowChange, true)
    resetDragSurfaceFullBleed(dragSurface)
  }
}
